import type { ChildId } from './valueObjects/childId';
import type { MeasureDateTime } from './valueObjects/measureDateTime';
import type { MeasurerId } from './valueObjects/measurerId';
import type { MeasurerType } from './valueObjects/measurerType';
import type { ParentId } from './valueObjects/parentId';
import type { SleepJsonPub, SleepJsonPvt } from '@/types/domain/sleep/entities/sleepEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 睡眠プロパティプライベート
 */
export type SleepPropsPvt = {
  /** 就寝時間かどうか */
  isSleepTime: boolean,
  /** 測定者ID */
  measurerId: MeasurerId,
  /** 測定タイプ */
  measurerType: MeasurerType,
  /** 測定日時 */
  measureDateTime: MeasureDateTime,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
}

/**
 * 睡眠プロパティ公開
 */
export type SleepPropsPub = Record<string, never>

/**
 * 睡眠Entity
 */
export class SleepEntity extends EntityPub<
  SleepPropsPvt,
  SleepJsonPvt,
  SleepPropsPub,
  SleepJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): SleepJsonPvt {
    return {
      isSleepTime: this.isSleepTime,
      measurerId: this.measurerId.value,
      measurerType: this.measurerType.value,
      measureDateTime: this.measureDateTime.value.toJSON(),
      parentId: this.parentId.value,
      childId: this.childId.value,
    };
  }

  toJsonPubData(): SleepJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurserySleepSleepWakeupEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.measureDateTime.toStringDateTime()}#${this.parentId.value}_${this.childId.value}#${this.isSleepTime ? '0' : '1'}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.measureDateTime.toStringDateTime()}#${this.isSleepTime ? '0' : '1'}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 就寝時間かどうか
   */
  get isSleepTime(): boolean {
    return this._value.isSleepTime;
  }

  /**
   * 測定者ID
   */
  get measurerId(): MeasurerId {
    return this._value.measurerId;
  }

  /**
   * 測定タイプ
   */
  get measurerType(): MeasurerType {
    return this._value.measurerType;
  }

  /**
   * 測定日時
   */
  get measureDateTime(): MeasureDateTime {
    return this._value.measureDateTime;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }
}
